/**
 * iter-386u-aa — Vendor Running Bills
 *
 * Cross-project list of every running bill the vendor has raised. Rows are
 * grouped by project (mirroring the Admin's Project Accounts high-end layout)
 * with a per-project totals footer. Standalone bills (no project) appear
 * under their own "Unattached" group with a one-click "Attach to Project"
 * action that reuses the existing attach-to-project endpoint.
 *
 * Row actions: Open (deep-link into VendorSubmissions detail), Cancel
 * (undoable within 15s), Amend (submit → draft), and Attach to Project
 * (unattached bills only).
 */
import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import {
  Receipt, Search, ArrowLeft, Building2, ExternalLink, Link2,
} from 'lucide-react';

const statusColours = {
  draft: 'bg-stone-100 text-stone-700 border-stone-300',
  submitted: 'bg-blue-50 text-blue-700 border-blue-300',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  rejected: 'bg-rose-50 text-rose-700 border-rose-300',
  cancelled: 'bg-stone-50 text-stone-400 border-stone-200 line-through',
  pending_amendment: 'bg-amber-50 text-amber-800 border-amber-300',
};

const money = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function VendorRunningBills() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // iter-386u-aa — Attach-to-Project dialog state (for bills with no project_id)
  const [projects, setProjects] = useState([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);
  const [attachProjectId, setAttachProjectId] = useState('');
  const [attachSaving, setAttachSaving] = useState(false);

  // iter-386u-aa — Undo cancel state. Latest cancel gets a 15-sec window to undo.
  const undoTimerRef = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        // iter-386u-aa — use the unified vendor/running-bills endpoint so
        // share-link auto-bills (created on quote approval) and admin RA bills
        // both show up alongside the vendor's own submission bills.
        axios.get(`${API}/vendor/running-bills`, { headers: authHeaders() }),
        axios.get(`${API}/projects`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      ]);
      const list = Array.isArray(r1.data?.bills) ? r1.data.bills : [];
      setBills(list);
      setProjects(Array.isArray(r2.data) ? r2.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load vendor running bills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const grandTotal = (b) => {
    // Prefer server-provided amount (works uniformly across submission bills,
    // share-link bills, and admin RA bills). Fall back to items sum for older
    // rows that didn't set an amount.
    if (typeof b.amount === 'number' && b.amount > 0) return b.amount;
    return (b.items || []).reduce((s, it) => {
      if (it.item_type === 'header' || it.is_header) return s;
      const aq = parseFloat(it.actual_quantity ?? it.quantity ?? 0) || 0;
      const ar = parseFloat(it.actual_rate ?? it.rate ?? 0) || 0;
      return s + aq * ar;
    }, 0);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (bills || []).filter(b => {
      if (statusFilter !== 'all' && (b.status || 'draft') !== statusFilter) return false;
      if (!q) return true;
      return (
        (b.title || '').toLowerCase().includes(q) ||
        (b.vendor_name || '').toLowerCase().includes(q) ||
        (b.project_name || '').toLowerCase().includes(q) ||
        (b.id || '').toLowerCase().includes(q)
      );
    });
  }, [bills, search, statusFilter]);

  // iter-386u-aa — Group by project (mirroring Admin Project Accounts layout).
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(b => {
      const key = b.project_id || '__unattached__';
      const label = b.project_name || 'Unattached (Standalone Bills)';
      if (!map.has(key)) map.set(key, { key, label, project_id: b.project_id, bills: [] });
      map.get(key).bills.push(b);
    });
    // Sort: unattached last, real projects first alphabetical
    return [...map.values()].sort((a, b) => {
      if (a.key === '__unattached__') return 1;
      if (b.key === '__unattached__') return -1;
      return a.label.localeCompare(b.label);
    });
  }, [filtered]);

  const totalsByStatus = useMemo(() => {
    const counts = { all: bills.length, draft: 0, submitted: 0, approved: 0, rejected: 0, cancelled: 0 };
    bills.forEach(b => { counts[b.status || 'draft'] = (counts[b.status || 'draft'] || 0) + 1; });
    return counts;
  }, [bills]);

  const performUndo = async (billId) => {
    try {
      await axios.post(`${API}/vendor/running-bills/${billId}/undo-cancel`, {}, { headers: authHeaders() });
      toast.success('Cancellation reverted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not undo — window expired');
    }
  };

  // iter-386u-ab — Two-step amendment. Vendor supplies the new total; server
  // freezes old amount + stages the new one as pending_amendment.
  const promptAmend = async (b) => {
    const currentAmount = grandTotal(b);
    const raw = window.prompt(
      `Amend running bill "${b.title || b.id}".\n\n` +
      `Current amount: ₹${currentAmount.toLocaleString('en-IN')}\n\n` +
      `Enter the new total you want to bill (submit for admin approval):`,
      String(Math.round(currentAmount)),
    );
    if (raw === null) return;
    const parsed = Number(String(raw).replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const reason = window.prompt('Reason for amendment (optional):', '') || '';
    try {
      await axios.post(
        `${API}/vendor/running-bills/${b.id}/amend`,
        { pending_amount: parsed, reason },
        { headers: authHeaders() },
      );
      toast.success('Amendment sent for admin approval. Old amount stays in ledger until they decide.');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit amendment');
    }
  };

  const formalize = async (id) => {
    try {
      await axios.post(`${API}/vendor-submissions/${id}/formalize-to-ra-bill`, {}, { headers: authHeaders() });
      toast.success('RA bill formalized into the project ledger');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to formalize');
    }
  };

  const handleCancel = async (id, title) => {
    if (!window.confirm(`Cancel running bill "${title || id}"?\nYou'll have 30 seconds to undo.`)) return;
    try {
      const resp = await axios.post(`${API}/vendor/running-bills/${id}/cancel`, {}, { headers: authHeaders() });
      // iter-386u-aa+ — derive the toast duration from the server-provided
      // undo_expires_at so client + server stay in sync (server grace = 30s).
      let graceMs = 30000;
      const expires = resp.data?.undo_expires_at;
      if (expires) {
        const remainingMs = new Date(expires).getTime() - Date.now();
        if (remainingMs > 0) graceMs = remainingMs;
      }
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      toast('Running bill cancelled', {
        description: `Tap Undo within ${Math.round(graceMs / 1000)} seconds to restore it.`,
        action: {
          label: 'Undo',
          onClick: () => performUndo(id),
        },
        duration: graceMs,
      });
      undoTimerRef.current = setTimeout(() => {
        undoTimerRef.current = null;
      }, graceMs);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleAmend = null; // deprecated — replaced by promptAmend for two-step workflow
  void handleAmend;

  const openAttach = (b) => {
    setAttachTarget(b);
    setAttachProjectId('');
    setAttachOpen(true);
  };

  const submitAttach = async () => {
    if (!attachTarget || !attachProjectId) {
      toast.error('Pick a project first');
      return;
    }
    setAttachSaving(true);
    try {
      await axios.post(
        `${API}/vendor-submissions/${attachTarget.id}/attach-to-project`,
        { project_id: attachProjectId },
        { headers: authHeaders() },
      );
      toast.success('Attached to project — now visible in that ledger');
      setAttachOpen(false);
      setAttachTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to attach');
    } finally {
      setAttachSaving(false);
    }
  };

  const renderRow = (b) => {
    const status = b.status || 'draft';
    const isUnattached = !b.project_id;
    const isApproved = status === 'approved';
    const isPendingAmendment = status === 'pending_amendment';
    const pendingAmount = b.pending_amendment_amount ?? null;
    const currentAmount = grandTotal(b);
    // "vendor_submission_bill" is the source that supports formalize-to-ra-bill
    const isSubmissionBill = b.source === 'vendor_submission_bill';
    const alreadyFormalized = !!b.formalized_ra_bill_id;
    return (
      <tr key={b.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`vendor-rb-row-${b.id}`}>
        <td className="p-3 font-medium text-stone-800">
          <div>{b.title || `Bill ${b.id?.slice(0, 8)}`}</div>
          {isPendingAmendment && pendingAmount != null && (
            <div
              className="mt-1 inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
              data-testid={`vendor-rb-amend-banner-${b.id}`}
            >
              <span className="uppercase font-semibold tracking-wide">Amendment pending</span>
              <span>{money(currentAmount)} <span className="text-amber-600">→</span> {money(pendingAmount)}</span>
            </div>
          )}
        </td>
        <td className="p-3 text-stone-700"><span className="inline-flex items-center gap-1"><Building2 size={12} className="text-stone-400" />{b.vendor_name || '—'}</span></td>
        <td className="p-3 text-right text-stone-800 font-semibold">{money(currentAmount)}</td>
        <td className="p-3 text-center">
          <Badge className={`text-[10px] uppercase border ${statusColours[status] || statusColours.draft}`}>{status.replace('_', ' ')}</Badge>
        </td>
        <td className="p-3 text-xs text-stone-500">{b.submitted_at ? formatDate(b.submitted_at) : '—'}</td>
        <td className="p-3 text-center">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => navigate(`/vendor-submissions?focus=${b.id}`)} data-testid={`vendor-rb-open-${b.id}`}>
              <ExternalLink size={12} className="mr-1" />Open
            </Button>
            {isUnattached && status !== 'cancelled' && (
              <Button
                size="sm"
                variant="outline"
                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                onClick={() => openAttach(b)}
                data-testid={`vendor-rb-attach-${b.id}`}
              >
                <Link2 size={12} className="mr-1" />Attach to Project
              </Button>
            )}
            {isApproved && !isUnattached && !alreadyFormalized && isSubmissionBill && (
              <Button
                size="sm"
                variant="outline"
                className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                onClick={() => formalize(b.id)}
                data-testid={`vendor-rb-formalize-${b.id}`}
              >
                <Link2 size={12} className="mr-1" />Attach to Project
              </Button>
            )}
            {(status === 'submitted' || isApproved || isPendingAmendment) && isSubmissionBill && (
              <Button
                size="sm"
                variant="outline"
                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={() => promptAmend(b)}
                data-testid={`vendor-rb-amend-${b.id}`}
              >
                Amend
              </Button>
            )}
            {(status === 'submitted' || status === 'draft') && (
              <Button
                size="sm"
                variant="outline"
                className="text-rose-700 border-rose-300 hover:bg-rose-50"
                onClick={() => handleCancel(b.id, b.title)}
                data-testid={`vendor-rb-cancel-${b.id}`}
              >
                Cancel
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-running-bills-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="vendor-rb-back-btn">
                <ArrowLeft size={14} className="mr-1" />Back
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <Receipt className="text-purple-600" size={24} />
              Vendor Running Bills
            </h1>
            <p className="text-stone-500 text-sm">Grouped by project. Attach any Unattached bill to a project to formalize it in the ledger.</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[280px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="vendor-rb-search"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by bill title, vendor, project…"
              className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-md text-sm"
            />
          </div>
          {[
            ['all', 'All'],
            ['draft', 'Draft'],
            ['submitted', 'Submitted'],
            ['approved', 'Approved'],
            ['rejected', 'Rejected'],
            ['cancelled', 'Cancelled'],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setStatusFilter(k)}
              data-testid={`vendor-rb-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                statusFilter === k
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'
              }`}
            >
              {label} <span className="ml-1 opacity-70">{totalsByStatus[k] ?? 0}</span>
            </button>
          ))}
        </div>

        {/* Grouped list */}
        {loading ? (
          <div className="p-10 text-center text-stone-400 bg-white border border-stone-200 rounded-lg">Loading…</div>
        ) : grouped.length === 0 ? (
          <div className="p-10 text-center text-stone-400 bg-white border border-stone-200 rounded-lg" data-testid="vendor-rb-empty">
            No vendor running bills match the current filter.
          </div>
        ) : (
          grouped.map(group => {
            const groupTotal = group.bills.reduce((s, b) => s + grandTotal(b), 0);
            const isUnattached = group.key === '__unattached__';
            return (
              <div key={group.key} className="bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid={`vendor-rb-group-${group.key}`}>
                <div className={`px-4 py-3 flex items-center justify-between ${isUnattached ? 'bg-amber-50 border-b border-amber-200' : 'bg-stone-50 border-b border-stone-200'}`}>
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className={isUnattached ? 'text-amber-700' : 'text-stone-600'} />
                    <span className={`font-semibold ${isUnattached ? 'text-amber-900' : 'text-stone-800'}`}>{group.label}</span>
                    <Badge variant="outline" className="text-[10px]">{group.bills.length} bill{group.bills.length !== 1 ? 's' : ''}</Badge>
                  </div>
                  <div className="text-sm text-stone-700">
                    <span className="text-xs text-stone-500 mr-1">{isUnattached ? 'Standalone total:' : 'Project total:'}</span>
                    <span className="font-bold" data-testid={`vendor-rb-group-total-${group.key}`}>{money(groupTotal)}</span>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-white text-xs uppercase text-stone-500 border-b border-stone-100">
                    <tr>
                      <th className="text-left p-3 font-semibold">Title</th>
                      <th className="text-left p-3 font-semibold">Vendor</th>
                      <th className="text-right p-3 font-semibold">Grand Total</th>
                      <th className="text-center p-3 font-semibold">Status</th>
                      <th className="text-left p-3 font-semibold">Submitted</th>
                      <th className="text-center p-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.bills.map(renderRow)}
                  </tbody>
                </table>
              </div>
            );
          })
        )}
      </div>

      {/* Attach-to-project dialog */}
      <Dialog open={attachOpen} onOpenChange={(o) => !o && setAttachOpen(false)}>
        <DialogContent className="max-w-md" data-testid="vendor-rb-attach-dialog">
          <DialogHeader>
            <DialogTitle>Attach Bill to a Project</DialogTitle>
            <DialogDescription>
              This will link &quot;{attachTarget?.title || 'the bill'}&quot; to a project so it becomes a Billed transaction in that ledger immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm font-medium text-stone-700">Project</label>
            <select
              value={attachProjectId}
              onChange={(e) => setAttachProjectId(e.target.value)}
              className="w-full h-9 border border-stone-300 rounded px-2 text-sm"
              data-testid="vendor-rb-attach-project-select"
            >
              <option value="">— pick a project —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setAttachOpen(false)}>Cancel</Button>
            <Button
              onClick={submitAttach}
              disabled={attachSaving || !attachProjectId}
              data-testid="vendor-rb-attach-submit"
            >
              {attachSaving ? 'Attaching…' : 'Attach'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
